import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/stores/authStore";
import { UploadScanTab } from "@/components/UploadScanTab";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  ArrowLeft, Shield, Upload, Scan, Lock, FileCode, 
  CheckCircle2, AlertTriangle, Info
} from "lucide-react";

export const ScanPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [showAuth, setShowAuth] = useState(false);

  // If not authenticated, show login prompt
  if (!token) {
    return (
      <div className="min-h-screen bg-[#050505]">
        {/* Header */}
        <header className="border-b border-white/5 bg-[#09090b]/80 backdrop-blur-lg sticky top-0 z-50">
          <div className="max-w-6xl mx-auto px-4 md:px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-zinc-400 hover:text-white"
                  onClick={() => navigate('/')}
                  data-testid="back-home-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Home
                </Button>
                <div className="w-px h-6 bg-white/10" />
                <div className="flex items-center gap-3 group">
                  <div className="relative">
                    <div className="absolute inset-0 bg-[#00f0ff]/20 blur-xl rounded-full scale-75 group-hover:scale-100 transition-transform" />
                    <img src="/zerozone-logo.png" alt="ZeroZone Logo" className="relative w-28 h-28 object-contain drop-shadow-[0_0_18px_rgba(59,130,246,0.5)]" />
                  </div>
                  <span className="text-white font-semibold">Bring Your Code</span>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Login Required Message */}
        <main className="max-w-2xl mx-auto px-4 md:px-6 py-16">
          <Card className="bg-[#09090b] border-white/10">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-[#00f0ff]/10 flex items-center justify-center mx-auto mb-6">
                <Lock className="w-8 h-8 text-[#00f0ff]" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-3">Sign In Required</h2>
              <p className="text-zinc-400 mb-6">
                Please sign in to upload and scan your Infrastructure as Code files.
              </p>
              <div className="flex gap-3 justify-center">
                <Button
                  onClick={() => navigate('/')}
                  className="bg-[#00f0ff] text-black hover:bg-[#00f0ff]/90"
                  data-testid="sign-in-btn"
                >
                  Sign In
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/guide')}
                  className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                >
                  Learn More
                </Button>
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="border-b border-white/5 bg-[#09090b]/80 backdrop-blur-lg sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                className="text-zinc-400 hover:text-white"
                onClick={() => navigate('/')}
                data-testid="back-home-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Home
              </Button>
              <div className="w-px h-6 bg-white/10" />
              <div className="flex items-center gap-3 group">
                <div className="relative">
                  <div className="absolute inset-0 bg-[#00f0ff]/20 blur-xl rounded-full scale-75 group-hover:scale-100 transition-transform" />
                  <img src="/zerozone-logo.png" alt="ZeroZone Logo" className="relative w-28 h-28 object-contain drop-shadow-[0_0_18px_rgba(59,130,246,0.5)]" />
                </div>
                <span className="text-white font-semibold">Bring Your Code</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                className="text-zinc-400 hover:text-white"
                onClick={() => navigate('/projects')}
              >
                My Projects
              </Button>
              <div className="text-sm text-zinc-500">
                {user?.email}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="border-b border-white/5 bg-gradient-to-b from-[#09090b] to-[#050505]">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-8">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
                Bring Your Own Code (BYOC)
              </h1>
              <p className="text-zinc-400">
                Upload & scan your existing Terraform, CloudFormation, Bicep, or Kubernetes files for security issues
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {["Checkov", "tfsec", "Trivy", "GitLeaks"].map((tool) => (
                <span key={tool} className="text-xs bg-purple-500/20 text-purple-400 px-2 py-1 rounded-full">
                  {tool}
                </span>
              ))}
            </div>
          </div>

          {/* Feature highlights */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-zinc-900/50 border border-zinc-800">
              <Shield className="w-5 h-5 text-emerald-400" />
              <div>
                <p className="text-sm text-white font-medium">4 Security Tools</p>
                <p className="text-xs text-zinc-500">Enterprise-grade scanning</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-zinc-900/50 border border-zinc-800">
              <FileCode className="w-5 h-5 text-cyan-400" />
              <div>
                <p className="text-sm text-white font-medium">Multi-Format Support</p>
                <p className="text-xs text-zinc-500">.tf, .yaml, .json, .bicep</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-zinc-900/50 border border-zinc-800">
              <CheckCircle2 className="w-5 h-5 text-yellow-400" />
              <div>
                <p className="text-sm text-white font-medium">Instant Results</p>
                <p className="text-xs text-zinc-500">Remediation guidance included</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - Upload & Scan Tab */}
      <main className="max-w-6xl mx-auto px-4 md:px-6 py-8">
        <UploadScanTab />
      </main>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-white/5">
        <div className="max-w-6xl mx-auto text-center text-zinc-600 text-sm">
          ZeroZone Global Resilience Cloud Architect - IaC Security Scanner
        </div>
      </footer>
    </div>
  );
};

export default ScanPage;
